import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import './Chatroom.css';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FaPaperPlane } from 'react-icons/fa'; // Import Send Icon
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import { BiCheck, BiCheckDouble } from 'react-icons/bi';
import { useSwipeable } from 'react-swipeable';
import SwipeableMessage from './SwipeableMessage';


const Chatroom = ({ currentUser, selectedProfile, onClose, userId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null); // ✅ Manage which dropdown is open
  const dropdownRef = useRef(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const chatMessagesRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [status, setStatus] = useState('Offline');
  const [lastSeen, setLastSeen] = useState(null);
  const [lastSeenTime, setLastSeenTime] = useState(null);
  const [replyToMessage, setReplyToMessage] = useState(null);


  const socket = io('https://back.varankal.com', {
    query: { userId: currentUser.userId },
    transports: ['websocket'],
  });

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return 'Last seen unavailable';

    const seenDate = new Date(lastSeen);
    const now = new Date();

    const diffInSeconds = Math.floor((now - seenDate) / 1000);

    // ✅ If last seen was within the last 60 seconds
    if (diffInSeconds <= 60) {
      return 'Online';
    }

    if (isToday(seenDate)) {
      return `Today at ${format(seenDate, 'hh:mm a')}`;
    }

    if (isYesterday(seenDate)) {
      return `Yesterday at ${format(seenDate, 'hh:mm a')}`;
    }

    const daysDifference = Math.floor(diffInSeconds / (60 * 60 * 24));

    if (daysDifference < 7) {
      return `${formatDistanceToNow(seenDate, { addSuffix: true })}`;
    }

    return `on ${format(seenDate, 'dd MMM yyyy')} at ${format(seenDate, 'hh:mm a')}`;
  };

  useEffect(() => {
    const fetchLastSeen = async () => {
      try {
        const res = await axios.get(
          `https://back.varankal.com/auth/last-seen/${selectedProfile.userId}`,
          { withCredentials: true }
        );
        setLastSeen(res.data.lastSeen);
        setIsOnline(false); // assume offline until socket says otherwise
      } catch (err) {
        console.error("Error fetching lastSeen", err);
      }
    };

    fetchLastSeen();

    socket.on('update-user-status', (data) => {
      if (data.userId === selectedProfile.userId) {
        setIsOnline(data.isOnline);
        if (data.isOnline) {
          setLastSeen(null); // ✅ clear it if user is online
        } else if (data.lastSeen) {
          setLastSeen(data.lastSeen);
        }
      }
    });

    return () => socket.off('update-user-status');
  }, [selectedProfile.userId]);

  useEffect(() => {
    if (!currentUser?.userId || !selectedProfile?.userId) return;

    const fetchMessages = async () => {
      try {
        const response = await axios.get(
          `https://back.varankal.com/chats/messages/${currentUser.userId}/${selectedProfile.userId}`
        );
        setMessages(response.data);
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
    };

    socket.on('messagesSeen', () => {
      fetchMessages();
    });


    const markAsDelivered = async () => {
      try {
        await axios.put('https://back.varankal.com/chats/mark-delivered', {
          senderId: selectedProfile.userId, // sender of messages you're receiving
          receiverId: currentUser.userId,   // current user is receiver
        });
      } catch (err) {
        console.error('Failed to mark as delivered:', err);
      }
    };

    // Initial fetch + mark delivered
    fetchMessages();
    markAsDelivered();

    // Set interval for polling every 2 seconds
    const intervalId = setInterval(() => {
      fetchMessages();
      markAsDelivered();
    }, 500);

    // Join socket room
    socket.emit('join', currentUser.userId);

    // Listen for new incoming messages via socket
    socket.on('message', (message) => {
      setMessages((prevMessages) => [...prevMessages, message]);
    });

    // Cleanup on unmount or dependency change
    return () => {
      clearInterval(intervalId);
      socket.off('message');
    };
  }, [currentUser.userId, selectedProfile.userId]);



  useEffect(() => {
    if (!selectedProfile || !currentUser) return;

    const timer = setTimeout(() => {
      // mark as seen
      const markAsSeen = async () => {
        try {
          await axios.put('https://back.varankal.com/chats/mark-seen', {
            senderId: selectedProfile.userId, // sender
            receiverId: currentUser.userId,   // receiver
          });
        } catch (err) {
          console.error('Failed to mark as seen:', err);
        }
      };

      markAsSeen();
    }, 500); // Wait 2 seconds to simulate read
    return () => clearTimeout(timer);
  }, [selectedProfile.userId, currentUser.userId]);


  socket.on('messagesSeen', ({ receiverId, senderId }) => {
    if (currentUser.userId === senderId && selectedProfile.userId === receiverId) {
      setMessages((prevMessages) => {
        const updated = prevMessages.map((msg) =>
          msg.senderId === senderId && msg.receiverId === receiverId && (msg.status === 'sent' || msg.status === 'delivered')
            ? { ...msg, status: 'seen' }
            : msg
        );
        // Only update state if changed, otherwise return prevMessages to avoid infinite render
        if (JSON.stringify(updated) !== JSON.stringify(prevMessages)) {
          return updated;
        } else {
          return prevMessages;
        }
      });
    }
  });


  const handleSendMessage = async () => {
    if (newMessage.trim() === '') return;

    const message = {
      senderId: currentUser.userId,
      receiverId: selectedProfile.userId,
      message: newMessage,
      senderName: currentUser.name,
      receiverName: selectedProfile.name,
      replyTo: replyToMessage ? replyToMessage._id : null // 🆕 add this line
    };

    try {
      const response = await axios.post('https://back.varankal.com/chats/send', message);

      setMessages((prevMessages) => [...prevMessages, response.data.data]);
      setNewMessage('');
      setReplyToMessage(null); // 🆕 clear reply state after sending

      socket.emit('chat message', response.data.data);

      await axios.post(`https://back.varankal.com/auth/update-last-seen/${currentUser.userId}`);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleDeleteForMe = async (messageId) => {
    try {
      await axios.post('https://back.varankal.com/chats/deleteForMe', {
        messageId,
        userId: currentUser.userId,
      });
      setMessages((prevMessages) =>
        prevMessages.filter((msg) => msg._id !== messageId)
      );
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleDeleteForEveryone = async (messageId) => {
    try {
      await axios.post('https://back.varankal.com/chats/deleteForEveryone', {
        messageId
      });
      setMessages((prevMessages) =>
        prevMessages.filter((msg) => msg._id !== messageId)
      );
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();

    if (date.toDateString() === now.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === new Date(now.setDate(now.getDate() - 1)).toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const groupMessagesByDate = () => {
    const groupedMessages = {};

    messages.forEach((msg) => {
      const dateLabel = formatDate(msg.timestamp);
      if (!groupedMessages[dateLabel]) {
        groupedMessages[dateLabel] = [];
      }
      groupedMessages[dateLabel].push(msg);
    });

    return groupedMessages;
  };

  const groupedMessages = groupMessagesByDate();

  const toggleDropdown = (id) => {
    setOpenDropdown((prev) => (prev === id ? null : id)); // ✅ Proper toggle logic
  };

  // ✅ Close Dropdown When Clicked Outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".message-options")) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (chatMessagesRef.current && !isUserScrolling) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight; // ✅ Always scroll to latest message
    }
  }, [messages]); // ✅ Scrolls only when messages change

  // ✅ Detect user scroll to stop auto-scrolling
  const handleScroll = () => {
    if (chatMessagesRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatMessagesRef.current;
      const isAtBottom = scrollHeight - scrollTop <= clientHeight + 50; // Small threshold

      setIsUserScrolling(!isAtBottom); // ✅ Stop auto-scroll when user scrolls up
    }
  };


  return (
    <div className="chatroom-container">
      <div className="chat-header33">
        <h3 className="chatt-header">
          {selectedProfile.profilePicture && (
            <img
              src={`data:image/jpeg;base64,${selectedProfile.profilePicture}`}
              alt="User Avatar"
              className="chat-header-avatar"
            />
          )}
          {selectedProfile.name}
        </h3>
        <div className='lastseennn'>
          <p>
            {isOnline || formatLastSeen(lastSeen) === 'Online'
              ? 'Online'
              : lastSeen
                ? `Last seen ${formatLastSeen(lastSeen)}`
                : 'Checking status...'}
          </p>
        </div>


        <FontAwesomeIcon icon={faCircleXmark} className="close-icoon" onClick={onClose} />
      </div>
      <div className="chat-messages" ref={chatMessagesRef} onScroll={handleScroll}>

        {Object.keys(groupedMessages)
          .sort((a, b) => new Date(a) - new Date(b)) // Sort dates in ascending order
          .map((dateLabel, index) => (
            <div key={index}>
              <div className="chat-date-label">{dateLabel}</div>
              {groupedMessages[dateLabel]
                .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                .map((msg, msgIndex) => (
                  <SwipeableMessage
                    key={msg._id}
                    msg={msg}
                    currentUser={currentUser}
                    openDropdown={openDropdown}
                    toggleDropdown={toggleDropdown}
                    handleDeleteForMe={handleDeleteForMe}
                    handleDeleteForEveryone={handleDeleteForEveryone}
                    setReplyToMessage={setReplyToMessage}
                    formatTime={formatTime}
                  />
                ))}


            </div>

          ))}

      </div>
      {replyToMessage && (
        <div className="reply-preview">
          <div>
            <strong>{replyToMessage.senderName}</strong>: {replyToMessage.message}
          </div>
          <button onClick={() => setReplyToMessage(null)}>✖</button>
        </div>
      )}



      <div className="chat-input">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
        />
        <button onClick={handleSendMessage}>
          <FaPaperPlane size={20} color="#fff" />
        </button>
      </div>

    </div>
  );
};

export default Chatroom; 